/* eslint-disable @next/next/no-img-element */
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useVideo } from 'react-use';
import useTranslation from 'next-translate/useTranslation';

// Components
import Icon from '@/components/base/Icon';

type SimpleVideoPlayerType = {
    thumbnail?: string;
    thumbnailIsUpdated?: boolean;
    video: string;
    played?: boolean;
    isLoading?: boolean;
    playCallback?: () => void;
    pauseCallback?: () => void;
    lightMode?: boolean;
    className?: string;
    videoClassName?: string;
    displayIconsOnHover?: boolean;
    handleErrorExternally?: () => void;
};

const SimpleVideoPlayer = ({
    thumbnail,
    thumbnailIsUpdated = false,
    video: videoUrl,
    played = false,
    isLoading = false,
    pauseCallback,
    playCallback,
    lightMode = false,
    className,
    videoClassName,
    displayIconsOnHover = true,
    handleErrorExternally
}: SimpleVideoPlayerType) => {
    const { t } = useTranslation();
    const [errorVideo, setErrorVideo] = useState(false);
    const [thumbnailDisplayed, setThumbnailDisplayed] = useState(true);
    const [video, state, controls] = useVideo({
        src: videoUrl,
        className: clsx('w-full h-full', videoClassName, {
            invisible: thumbnailDisplayed
        }),
        type: 'video/mp4',
        onError: () => setErrorVideo(true)
    });

    const togglePlay = () => {
        if (state.paused) {
            controls.play();
            setThumbnailDisplayed(false);
            if (typeof playCallback === 'function') {
                playCallback();
            }
        } else {
            controls.pause();
            setThumbnailDisplayed(false);
            if (typeof pauseCallback === 'function') {
                pauseCallback();
            }
        }
    };

    useEffect(() => {
        if (thumbnailIsUpdated && !thumbnailDisplayed) {
            controls.seek(0);
            setThumbnailDisplayed(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thumbnailIsUpdated]);

    useEffect(() => {
        if (state.paused && played) {
            controls.play();
        }
        if (!state.paused && !played) {
            controls.pause();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [played]);

    useEffect(() => {
        if (!errorVideo || typeof handleErrorExternally !== 'function') {
            return undefined;
        }

        handleErrorExternally();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorVideo]);

    if (errorVideo) {
        return (
            <div className="flex h-full w-full flex-col items-center justify-center space-y-3 text-icoGray-500 dark:text-icoDarkMode-wolf">
                <span
                    className={clsx('flex h-12 w-12 items-center justify-center rounded-full', {
                        'bg-icoGray-100 dark:bg-icoDarkMode-deepBlack': lightMode,
                        'bg-icoGray-300 dark:bg-icoDarkMode-anthracite': !lightMode
                    })}>
                    <Icon icon="play" />
                </span>
                <p className="text-sm">{t('commons:video_not_loaded')}</p>
            </div>
        );
    }

    return (
        <div className={clsx('group relative', className)}>
            {video}
            <img
                src={thumbnail}
                className={clsx(
                    'absolute left-1/2 top-1/2 max-h-full max-w-full -translate-x-1/2 -translate-y-1/2',
                    {
                        hidden: !thumbnailDisplayed
                    }
                )}
                alt=""
            />
            {videoUrl ? (
                <button
                    disabled={isLoading}
                    className={clsx(
                        'btn-toggle-video absolute left-1/2 top-1/2 z-10 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full border border-gray-300 bg-icoGray-400/70 text-base text-white opacity-0 transition-opacity group-hover:opacity-100',
                        { 'opacity-100': !state.playing && !displayIconsOnHover }
                    )}
                    onClick={togglePlay}>
                    <Icon
                        icon={!state.paused ? 'pause ' : 'play'}
                        className="btn-icon align-bottom"
                    />
                    <span
                        className={clsx(
                            'absolute inset-0 -mx-0.5 -my-0.5 h-[calc(100%+4px)] w-[calc(100%+4px)] animate-spin rounded-full border-2 border-transparent border-r-white border-t-white',
                            {
                                hidden: !isLoading
                            }
                        )}
                    />
                </button>
            ) : null}
        </div>
    );
};

export default SimpleVideoPlayer;
